import { PROJECT_AUDIO_TYPES } from 'consts/audio'
import { sortArraysByKey } from 'store/features/projectData/utils'
import { TMultilineAudioItems } from 'store/types/audio'
import { ITtsItem, ITtsItemData, ITtsSpeaker } from 'types/text-to-speech'

export const filterTtsFromAudios = (
  multilineAudioItems: TMultilineAudioItems
) => multilineAudioItems.filter(item => item.type === PROJECT_AUDIO_TYPES.TTS)

// Speakers are cached per langCode. When a non-default voice model is used
// (e.g. the AI Avatar flow's `tts-worker`), the model is folded into the cache
// key so that flow and the regular TTS flow don't clobber each other's lists.
export const getSpeakersCacheKey = (langCode: string, model?: string) =>
  model ? `${langCode}::${model}` : langCode

export const getSortedTtsItemIdsFromAudios = (audios: TMultilineAudioItems) =>
  sortArraysByKey('projectStart', audios).map(audio => Number(audio.assetId))

export const reorderTtsItemsByIds = (
  items: ITtsItemData[],
  sortedIds: number[]
): ITtsItemData[] => {
  return items.sort((a, b) => {
    const aIndex = sortedIds.indexOf(a.id)
    const bIndex = sortedIds.indexOf(b.id)
    return aIndex - bIndex
  })
}

export const filterDeletedItems = (
  item: ITtsItemData,
  sortedIds: number[]
): ITtsItemData | null => {
  return sortedIds.includes(item.id) ? item : null
}

// to convert the response from ttsAudio to ttsItem ( ttsAudio has only data, not the states)
export const convertTtsAudioToTtsItem = (data: ITtsItemData): ITtsItem => ({
  data: { ...data },
  isGenerated: true,
  isLoading: false,
  isDraft: false
})

export const getFinalReorderedAndFilteredItems = (
  allItemsData: ITtsItemData[],
  sortedTtsAudiosIds: number[]
) => {
  return reorderTtsItemsByIds(allItemsData, sortedTtsAudiosIds)
    .map(item => filterDeletedItems(item, sortedTtsAudiosIds))
    .filter(item => item !== null)
    .map(convertTtsAudioToTtsItem)
}

export const checkIsPremiumVoice = (
  speakerId: number,
  speakers: ITtsSpeaker[]
): boolean =>
  speakers.find(speaker => speaker.id === speakerId)?.isPremium || false

const getFirstNonPremiumSpeakerId = (speakers: ITtsSpeaker[]): number => {
  const nonPremiumSpeaker = speakers.find(speaker => !speaker.isPremium)
  return nonPremiumSpeaker ? nonPremiumSpeaker.id : speakers[0]?.id
}

/**
 * For free users, returns the first non-premium speaker.
 * For paid users, returns the first speaker in the list.
 */
export const getFirstSpeakerIdForUser = (
  speakers: ITtsSpeaker[],
  isFreeUser: boolean
): number =>
  isFreeUser ? getFirstNonPremiumSpeakerId(speakers) : speakers[0]?.id

/**
 * Picks the first usable speaker matching the given gender (case-insensitive).
 * Falls back to the regular default speaker when no gender match exists.
 * Returns null if there are no speakers at all.
 */
export const getSpeakerIdByGender = (
  speakers: ITtsSpeaker[] | null,
  gender: string | null | undefined,
  isFreeUser: boolean
): number | null => {
  if (!speakers?.length) {
    return null
  }
  const normalizedGender = gender?.toLowerCase()
  const genderMatches = normalizedGender
    ? speakers.filter(
        speaker => speaker.gender?.toLowerCase() === normalizedGender
      )
    : []
  const pool = genderMatches.length ? genderMatches : speakers
  return getFirstSpeakerIdForUser(pool, isFreeUser) ?? null
}
